import React, { useState, useEffect } from "react";
import {
  Dropdown,
  Option,
  Button,
  Textarea,
  RadioGroup,
  Radio,
  Spinner,
} from "@fluentui/react-components";
import { PlayRegular, PauseRegular, Dismiss20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { VOICE_LIST } from "../GenericComponent/GenericData/voiceList";
import { generateAudioService } from "../../Services/HeedifyServices";
import Language from "./Language";

import "./CSS/GenerateAudio.css";

export default function GenerateAudio(props) {
  const { t } = useTranslation();

  // ✅ Normaliser le type : text2speech, Text2speech -> text2speech (minuscule)
  const normalizeType = (type) => {
    if (!type) return "text2speech";
    const lowerType = type.toLowerCase();
    if (lowerType === "text2speech") return "text2speech";
    if (lowerType === "audiofile") return "Audiofile";
    return type;
  };

  const initialType = normalizeType(props.type);
  const initialText = props.text || "";
  const [audio, setAudio] = useState(null);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [text, setText] = useState(initialText);
  const [type, setType] = useState(initialType);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  // Synchroniser le type avec les props quand elles changent
  useEffect(() => {
    const newType = normalizeType(props.type);
    setType(newType);
  }, [props.type]);

  // Synchroniser le texte avec les props
  useEffect(() => {
    setText(props.text || "");
  }, [props.text]);

  // Pause audio on unmount
  useEffect(() => {
    return () => {
      if (audio) audio.pause();
    };
  }, [audio]);

  // Pause audio whenever type changes
  useEffect(() => {
    if (audio) {
      audio.pause();
      setAudio(null);
      setCurrentPlaying(null);
    }
  }, [type]);

  // Play / Pause audio
  const handlePlayPause = (music) => {
    // Stop previous audio
    if (audio) {
      audio.pause();
      setAudio(null);
      setCurrentPlaying(null);
      if (currentPlaying?.header === music.header) return;
    }

    const newAudio = new Audio(music.url);
    setAudio(newAudio);
    setCurrentPlaying(music);
    
    // Play with error handling
    newAudio.play().catch((err) => {
      console.warn("Audio play interrupted:", err);
      setAudio(null);
      setCurrentPlaying(null);
    });
    
    newAudio.onended = () => {
      setAudio(null);
      setCurrentPlaying(null);
    };
  };

  // Delete current audio
  const handleDelete = (music) => {
    if (currentPlaying?.header === music.header && audio) {
      audio.pause();
      setAudio(null);
      setCurrentPlaying(null);
    }
    props.setDelete?.();
  };

  // Generate Text2Speech audio
  const generateAudioFile = () => {
    const selectedVoiceObj = VOICE_LIST.find(
      (v) => v.header === props.defaultLanguage
    );

    const data = {
      language: props.defaultLanguage,
      voice: selectedVoiceObj ? selectedVoiceObj.voice : "AriaNeural",
      text: text,
      directory: props.path,
    };

    setIsSaveLoading(true);
    generateAudioService(data)
      .then((url) => props.saveAudio?.(url))
      .catch((err) => console.error("Erreur génération audio:", err))
      .finally(() => setIsSaveLoading(false));
  };

  return (
    <div className={`generate-audio-container ${props.customClassName || ''}`}>
      {/* Radio to choose type */}
      <RadioGroup
        className="radio-group-style"
        layout="horizontal"
        value={type}
     disabled={props.disabled}
        onChange={(_, data) => {
          setType(data.value);
          props.setType?.(data.value);
        }}
      >
        <Radio value="text2speech" label="Text2speech" />
        <Radio value="Audiofile" label="Audio" />
      </RadioGroup>

      {/* Text2Speech */}
      {type === "text2speech" && (
        <>
          <div className="generate-audio-row">
            <Language
              language={props.defaultLanguage}
              setLangue={(newLang) => props.setLangue?.(newLang)}
              className="generate-audio-width"
                  disabled={props.disabled}
            />
          </div>

          {!props.hideTextarea && (
            <Textarea
              className="generate-audio-width"
              appearance="outline"
              placeholder={t("TYPE_HERE")}
              resize="both"
               disabled={props.disabled}
              value={text}
              onChange={(e, data) => {
                setText(data.value);
              }}
              onBlur={(e) => {
                props.setText?.(e.target.value);
              }}
            />
          )}

          <Button
            onClick={generateAudioFile}
            className="btn-generate-audio-style"
            appearance="primary"
             disabled={props.disabled}
          >
            {isSaveLoading && <Spinner size="tiny" />} {t("GENERATE_AUDIO")}
          </Button>
        </>
      )}

      {/* AudioFile */}
      {type === "Audiofile" && (
        <div className="generate-audio-row">
          <Dropdown
            className="generate-audio-width"
            placeholder={t("SELECT_MUSIC")}
            value={props.fileName || ""}
             disabled={props.disabled}
            onOptionSelect={(_, data) => {
              const selectedMedia = props.medias?.find(
                (media) => media.header === data.optionValue
              );

              if (selectedMedia) {
                // Only auto-play if autoPlayOnSelect is not explicitly false
                if (props.autoPlayOnSelect !== false) {
                  handlePlayPause({ header: selectedMedia.header, url: selectedMedia.url });
                }
                props.setAudio?.({
                  header: selectedMedia.header,
                  url: selectedMedia.url,
                });
              }
            }}
          >
            {props.medias?.map((item) => (
              <Option key={item.header} value={item.header}>
                {item.header}
              </Option>
            ))}
          </Dropdown>

          <div className="music-list generate-audio-width">
            {props.fileName && (
              <div className="music-item">
                <span className="music-name">{props.fileName}</span>

                <Button
                  appearance="subtle"
                  disabled={props.canPlayAudio === false}
                  icon={
                    currentPlaying?.header === props.fileName ? (
                      <PauseRegular />
                    ) : (
                      <PlayRegular />
                    )
                  }
                  onClick={() =>
                    handlePlayPause({ header: props.fileName, url: props.audioFileUrl })
                  }
                  title={
                    currentPlaying?.header === props.fileName ? t("PAUSE") : t("PLAY")
                  }
                />

                <Button
                  disabled={props.disabled}
                  appearance="subtle"
                  icon={<Dismiss20Regular />}
                  onClick={() =>
                    handleDelete({ header: props.fileName, url: props.audioFileUrl })
                  }
                  title={t("DELETE")}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
